import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown } from "@phosphor-icons/react"
import { requestSubscription } from "relay-runtime"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { TooltipContent, TooltipCustom, TooltipTrigger } from "@/components/ui/tooltip"
import { UserProvider } from "@/components/user-wrapper"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useToast } from "@/hooks/useToast"
import { useUnmount } from "@/hooks/useUnmount"
import { useUserStore } from "@/lib/fetch-users"
import { formatDateString } from "@/lib/format-date"
import { hinEpdCheckValidity, hinEpdLoginUrl } from "@/lib/hin-epd-login"
import { hinCheckValidity, hinLogin } from "@/lib/hin-login"
import { cn } from "@/lib/utils"
import { hasDeBetterDocMsnResult } from "@/modules/patients/detail/better-doc/de/lib/has-de-better-doc-msn-result"
import { hasDeBetterDocRecommendedCareProviders } from "@/modules/patients/detail/better-doc/de/lib/has-de-better-doc-recommended-care-providers"
import { deSigningSubscription as deSigningSubscriptionType } from "@/modules/patients/detail/conclude/services/de/__generated__/deSigningSubscription.graphql"
import { deSigningSubscription } from "@/modules/patients/detail/conclude/services/de/deSigningSubscription"
import { useDeCancelQesSigningMutation } from "@/modules/patients/detail/conclude/services/de/useDeCancelQesSigningMutation"
import { useDocumentSigning } from "@/modules/patients/detail/conclude/services/useDocumentSigning"
import { useTelematikInfrastructureDocumentSigning } from "@/modules/patients/detail/conclude/services/useTelematikInfrastructureDocumentSigning"
import { TelematikInfrastructureSignButton } from "@/modules/patients/detail/conclude/telematik-infrastructure-sign-button"
import { useChGeneratePrescriptionMutation$data } from "@/modules/patients/detail/prescription/ch/services/__generated__/useChGeneratePrescriptionMutation.graphql"
import {
  chElectronicPrescriptionHasHinEpdAuthenticationIssue,
  useChGeneratePrescriptionMutation,
} from "@/modules/patients/detail/prescription/ch/services/useChGeneratePrescriptionMutation"
import { CarePlanViewer } from "@/modules/patients/detail/progress-note/documents/care-plan-summary-document-preview"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import {
  CARE_PLAN_SUMMARY,
  CH_APPROVABLE_DOCUMENTS,
  CH_DOCUMENTS,
  CH_SIGNABLE_DOCUMENTS,
  DE_APPROVABLE_DOCUMENTS,
  DE_DOCUMENTS,
  DE_SIGNABLE_DOCUMENTS,
  QES_DOCUMENTS,
} from "@/modules/patients/types/ProgressNoteDocument"
import { RelayEnvironment } from "@/relay-environment"
import { SofiaEnvironment } from "@/sofia-environment"

interface MessageEvent {
  data: {
    success?: boolean
    action: "hin-logged" | "hin-epd-logged" | "hin-epd-failed-sso-gateway-user"
  }
}

interface PdfDocumentViewerProps {
  signedAt: string | null | undefined
  fileId: string
}

const PdfDocumentViewer = ({ signedAt, fileId }: PdfDocumentViewerProps) => {
  const countryCode = useCountryParam()
  if (!document) return null

  return (
    <div className="h-full flex-1" key={signedAt}>
      <embed
        className="h-full w-full"
        src={`${SofiaEnvironment.Bff.Url}/api/${countryCode.toLocaleLowerCase()}/files/${fileId}?embed=true#view=fitH`}
        type="application/pdf"
        data-testid="preview-document"
      />
    </div>
  )
}

interface DocumentViewerProps {
  onSigningSuccess?: (id: string) => void
  keepOriginalAuthor?: boolean
}

type ProcessingAction = "sign" | "electronic" | "approval" | "telematikSign"

const DocumentViewer = ({ onSigningSuccess, keepOriginalAuthor = false }: DocumentViewerProps) => {
  const { t } = useTranslation()
  const countryCode = useCountryParam()
  const document = useConcludeDialogStoreContext((state) => state.selectedDocument)
  const setSelectedDocument = useConcludeDialogStoreContext((state) => state.actions.setSelectedDocument)
  const [processingDocuments, setProcessingDocuments] = useState<Map<string, ProcessingAction>>(new Map())
  const [isPinEntryRequired, setIsPinEntryRequired] = useState(false)
  const disposeRef = useRef<() => void>(undefined)
  const isProcessingQES = useRef<boolean>(false)
  const [signingPopup, setSigningPopup] = useState<Window | null>(null)
  const [cancelDeQesSigning] = useDeCancelQesSigningMutation()

  const patientFileInfo = usePatientFileContext((state) => state.patientFileInfo)
  const patientData = usePatientFileContext((state) => state.patientData)
  const [chGeneratePrescriptionDocument] = useChGeneratePrescriptionMutation({
    considerErrorAsBlocking: (errorTypeName) => !chElectronicPrescriptionHasHinEpdAuthenticationIssue(errorTypeName),
  })

  const contactChannel = patientData?.contactChannel

  const { toast } = useToast()
  const signDocument = useDocumentSigning({ countryCode, keepOriginalAuthor })
  const signDocumentWithTelematikInfrastructure = useTelematikInfrastructureDocumentSigning()
  const loginUser = useUserStore((state) => state.loginUser)
  const signed = document?.signedAt && document.signedBy

  const progressNotes = useEpisodeOfCareStoreContext((state) => state.progressNoteAndParentData)
  const isBetterDocMsnProgressNote = progressNotes.some(
    ({ episodeOfCareProgressNoteData: { pointOfCare, betterDoc, id } }) =>
      pointOfCare && !pointOfCare.careProviderId && betterDoc?.isUsed && document?.progressNoteId === id,
  )

  const isDeBetterDocRecommendedCareProvidersProgressNote = progressNotes.some(
    ({ episodeOfCareProgressNoteData }) =>
      document?.progressNoteId === episodeOfCareProgressNoteData.id &&
      hasDeBetterDocRecommendedCareProviders(episodeOfCareProgressNoteData),
  )

  const isDeBetterDocMsnProgressNote = progressNotes.some(
    ({ episodeOfCareProgressNoteData }) =>
      document?.progressNoteId === episodeOfCareProgressNoteData.id &&
      hasDeBetterDocMsnResult(episodeOfCareProgressNoteData),
  )

  const renderViewer = () => {
    if (document?.renderFn) {
      return (
        <ScrollWrapper className="h-full">
          <document.renderFn {...document} />
        </ScrollWrapper>
      )
    }

    if (document?.fileId) {
      return <PdfDocumentViewer signedAt={document.signedAt} fileId={document.fileId} />
    }

    return null
  }

  const addProcessingDocument = (documentId: string, action: ProcessingAction) =>
    setProcessingDocuments((prevMap) => new Map(prevMap).set(documentId, action))

  const removeProcessedDocument = (documentId: string) =>
    setProcessingDocuments((prevMap) => {
      const newMap = new Map(prevMap)
      newMap.delete(documentId)
      return newMap
    })

  const completeChPrescriptionGeneration = useCallback(
    (response: useChGeneratePrescriptionMutation$data) => {
      const prescription = response.ch_generatePrescriptionDocument.ch_Prescription
      if (!document || !prescription?.id) {
        return
      }

      setSelectedDocument({
        ...document,
        signedAt: prescription.signedAt,
        signedBy: prescription.signedBy,
      })

      removeProcessedDocument(prescription.id)
    },
    [document, setSelectedDocument],
  )

  const handleElectronicPrescriptionAfterLoggedIn = useCallback(async () => {
    if (!document) {
      return
    }

    addProcessingDocument(document.id, "electronic")
    const readyToPrescribe = await hinEpdCheckValidity()
    if (!readyToPrescribe) {
      toast({ description: t("hin.epd.authenticationInternalIssue") })
      removeProcessedDocument(document.id)
      return
    }

    chGeneratePrescriptionDocument(true, {
      id: document.id,
      progressNoteId: document.progressNoteId,
      onError: () => {
        removeProcessedDocument(document.id)
      },
      onCompleted(response) {
        if (
          response.ch_generatePrescriptionDocument.errors?.some(
            (e) => e.__typename && chElectronicPrescriptionHasHinEpdAuthenticationIssue(e.__typename),
          )
        ) {
          toast({ description: t("hin.epd.authenticationInternalIssue") })
          return
        }

        completeChPrescriptionGeneration(response)
      },
    })
  }, [document, chGeneratePrescriptionDocument, toast, t, completeChPrescriptionGeneration])

  const handleElectronicPrescription = useCallback(async () => {
    if (!document) {
      return
    }

    const hindEpdLoginWindow = window.open("about:blank")
    if (!hindEpdLoginWindow) {
      toast({ description: t("hin.epd.newTabBlocked") })
      return
    }

    addProcessingDocument(document.id, "electronic")
    hindEpdLoginWindow.document.body.innerHTML = t("hin.epd.newTabInProgress")

    const readyToPrescribe = await hinEpdCheckValidity()
    if (!readyToPrescribe) {
      hindEpdLoginWindow.location = hinEpdLoginUrl
      return
    }

    chGeneratePrescriptionDocument(true, {
      id: document.id,
      progressNoteId: document.progressNoteId,
      onError: () => {
        hindEpdLoginWindow.close()
        removeProcessedDocument(document.id)
      },
      onCompleted(response) {
        if (
          response.ch_generatePrescriptionDocument.errors?.some(
            (e) => e.__typename && chElectronicPrescriptionHasHinEpdAuthenticationIssue(e.__typename),
          )
        ) {
          hindEpdLoginWindow.location = hinEpdLoginUrl
          return
        }

        hindEpdLoginWindow.close()

        completeChPrescriptionGeneration(response)
      },
    })
  }, [document, t, chGeneratePrescriptionDocument, toast, completeChPrescriptionGeneration])

  const handleSigning = useCallback(
    async (type: "approval" | "signing") => {
      if (!document?.id) return

      addProcessingDocument(document.id, type === "approval" ? "approval" : "sign")
      const readyToSign = type === "signing" ? await hinCheckValidity() : true

      if (readyToSign) {
        isProcessingQES.current = true

        signDocument({
          variables: {
            id: document.id,
            progressNoteId: document.progressNoteId,
            type: document.type,
          },
          onQESCallback({ url, requestId }) {
            setSelectedDocument({
              ...document,
              signRequestId: requestId,
            })

            const { dispose } = requestSubscription<deSigningSubscriptionType>(RelayEnvironment, {
              subscription: deSigningSubscription,
              variables: {
                signRequestId: requestId,
              },
              updater(store, data) {
                const de_documentSigned = data?.de_documentSigned
                const { documentId, signedBy, signedAt } = de_documentSigned || {}
                if (!documentId) return

                const documentRecordProxy = store.get(documentId)
                documentRecordProxy?.setValue(signedBy, "signedBy")
                documentRecordProxy?.setValue(signedAt, "signedAt")

                setSelectedDocument({
                  ...document,
                  signedAt,
                  signedBy,
                })

                removeProcessedDocument(document.id)
                onSigningSuccess?.(documentId)
              },
            })
            disposeRef.current = dispose

            if (isProcessingQES.current === true) {
              const popupWindow = window.open(
                url,
                "",
                [
                  `height=${window.screen.height}`,
                  `width=${window.screen.width}`,
                  `status="no"`,
                  `menuBar="no"`,
                  `titlebar="no"`,
                ].join(","),
              )
              setSigningPopup(popupWindow)
            }
          },
          onSuccess(data) {
            let documentData = data

            if (documentData.type === CARE_PLAN_SUMMARY) {
              documentData = {
                ...documentData,
                renderFn: () => (
                  <CarePlanViewer
                    id={documentData.id}
                    progressNoteId={documentData.progressNoteId}
                    fileId={documentData.fileId}
                    signedAt={documentData.signedAt}
                  />
                ),
              }
            }

            setSelectedDocument(documentData)
            removeProcessedDocument(document.id)
            onSigningSuccess?.(documentData.id)
          },
          onError() {
            removeProcessedDocument(document.id)
          },
        })
      } else {
        removeProcessedDocument(document.id)
        hinLogin()
      }
    },
    [document, onSigningSuccess, setSelectedDocument, signDocument],
  )

  const handleTelematikInfrastructureSigning = useCallback(() => {
    if (!document?.id) return

    setIsPinEntryRequired(false)
    addProcessingDocument(document.id, "telematikSign")

    signDocumentWithTelematikInfrastructure({
      variables: {
        id: document.id,
        progressNoteId: document.progressNoteId,
        type: document.type,
      },
      onPinEntryRequired() {
        setIsPinEntryRequired(true)
      },
      onSuccess(documentData) {
        setIsPinEntryRequired(false)
        setSelectedDocument(documentData)
        removeProcessedDocument(document.id)
        onSigningSuccess?.(documentData.id)
      },
      onError() {
        setIsPinEntryRequired(false)
        removeProcessedDocument(document.id)
      },
    })
  }, [document, onSigningSuccess, setSelectedDocument, signDocumentWithTelematikInfrastructure])

  const shouldBeApproval = useMemo(() => {
    switch (countryCode) {
      case "DE":
        return (
          document?.type && DE_APPROVABLE_DOCUMENTS.includes(document?.type) && (document.fileId || document.renderFn)
        )
      case "CH":
        return (
          document?.type && CH_APPROVABLE_DOCUMENTS.includes(document?.type) && (document.fileId || document.renderFn)
        )
      default:
        return false
    }
  }, [countryCode, document])

  const shouldBeSigning = useMemo(() => {
    switch (countryCode) {
      case "DE":
        return (
          document?.type && DE_SIGNABLE_DOCUMENTS.includes(document?.type) && (document.fileId || document.renderFn)
        )
      case "CH":
        return (
          document?.type && CH_SIGNABLE_DOCUMENTS.includes(document?.type) && (document.fileId || document.renderFn)
        )
      default:
        return false
    }
  }, [countryCode, document])

  const isQES = document?.type && QES_DOCUMENTS.includes(document.type) && countryCode === "DE"

  const canSignWithTelematikInfrastructure = countryCode === "DE" && Boolean(loginUser?.hasTelematikId)

  const isElectronicPrescription = countryCode === "CH" && document && document.type === "prescription"

  useEffect(() => {
    const messageCallback = (event: MessageEvent) => {
      if (event.data) {
        switch (event.data.action) {
          case "hin-logged":
            toast({
              description: t("hin.loginHinSuccessfully"),
            })
            handleSigning("signing")
            break
          case "hin-epd-logged":
            toast({
              description: t("hin.loginHinSuccessfully"),
            })
            handleElectronicPrescriptionAfterLoggedIn()
            break
          case "hin-epd-failed-sso-gateway-user":
            if (document?.id) {
              removeProcessedDocument(document.id)
            }
            break
        }
      }
    }
    window.addEventListener("message", messageCallback)

    return () => {
      window.removeEventListener("message", messageCallback)
    }
  }, [document?.id, handleSigning, handleElectronicPrescriptionAfterLoggedIn, signDocument, t, toast])

  const getDisplayedTextByChannel = () => {
    switch (contactChannel) {
      case "EMAIL_AND_SMS":
        return (
          <div className="flex items-center gap-1">
            <span className="whitespace-nowrap">{t("patient.conclude.thisDocumentWillBeSentTo")}</span>
            <TooltipCustom>
              <TooltipTrigger asChild>
                <span className="truncate">{patientFileInfo?.emailAddress}</span>
              </TooltipTrigger>
              <TooltipContent className="break-all" align="start">
                {patientFileInfo?.emailAddress}
              </TooltipContent>
            </TooltipCustom>
          </div>
        )
      case "APP":
      case "NO_CHANNEL":
        return t("patient.conclude.thisDocumentWillBeAvailableInApp")
      case undefined:
        return ""
    }
  }

  // The info where the documents will be sent
  const getDisplayedTextByCountry = () => {
    switch (countryCode) {
      case "DE":
        if (document?.type === DE_DOCUMENTS.REFERRAL_LETTER && isDeBetterDocMsnProgressNote) {
          return t("patient.referralLetter.documentInfoTextBetterDoc")
        }
        if (document?.type === DE_DOCUMENTS.REFERRAL_LETTER && !isDeBetterDocRecommendedCareProvidersProgressNote) {
          return ""
        }
        return t("patient.conclude.thisDocumentWillBeAvailableInApp")
      case "CH":
        if (document?.type === CH_DOCUMENTS.REFERRAL_LETTER) {
          if (isBetterDocMsnProgressNote) {
            return t("patient.referralLetter.documentInfoTextBetterDoc")
          }
          return t("patient.referralLetter.documentInfoText")
        }
        return getDisplayedTextByChannel()
      default:
        return ""
    }
  }

  const handleQESCancel = () => {
    if (!document?.id) return
    isProcessingQES.current = false

    removeProcessedDocument(document.id)

    if (signingPopup) {
      signingPopup.close()
      setSigningPopup(null)
    }

    if (document.signRequestId) {
      cancelDeQesSigning({
        variables: {
          input: {
            signingRequestId: document.signRequestId,
          },
        },
      })
    }
  }

  useUnmount(() => {
    if (disposeRef.current) {
      disposeRef.current()
    }
  })

  const processingAction = document?.id ? processingDocuments.get(document.id) : undefined

  return (
    <div className="my-4 flex flex-col overflow-hidden pr-2">
      <div className="mb-4 flex min-h-10 items-center justify-between gap-1">
        {(shouldBeSigning || shouldBeApproval) && (
          <p className="text-on-surface min-w-0 text-sm">
            {processingAction === "telematikSign" && isPinEntryRequired
              ? t("patient.conclude.enterPinAtCardTerminal")
              : getDisplayedTextByCountry()}
          </p>
        )}
        <div className="ml-auto">
          {signed && (
            <div className="flex items-center gap-2 [&>span]:h-8 [&>span]:w-8 [&>span]:text-xs">
              <UserProvider
                userName={document.signedBy}
                render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={user.firstName} size="md" />}
              />
              {document?.signedAt && (
                <p className="text-on-surface-subdued text-xs tracking-[0.25px]">
                  {shouldBeApproval ? t("patient.conclude.approvedOn") : t("patient.conclude.signedOn")}{" "}
                  {formatDateString(document.signedAt)}
                </p>
              )}
            </div>
          )}
          {!signed && (
            <>
              {shouldBeApproval && (
                <Button
                  data-testid="approve-button"
                  disabled={processingDocuments.size > 0}
                  className="min-w-26.25 px-6"
                  isLoading={processingAction === "approval"}
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("check_out_approve")
                    handleSigning("approval")
                  }}>
                  {t("patient.conclude.approve")}
                </Button>
              )}
              {shouldBeSigning && (
                <div className="flex">
                  {canSignWithTelematikInfrastructure && (
                    <TelematikInfrastructureSignButton
                      isDisabled={processingDocuments.size > 0}
                      isLoading={processingAction === "telematikSign"}
                      onSign={handleTelematikInfrastructureSigning}
                    />
                  )}

                  <Button
                    data-testid="sign-button"
                    disabled={processingDocuments.size > 0}
                    className={cn("min-w-19.5 px-6", isQES && "rounded-r-none")}
                    isLoading={processingAction === "sign"}
                    onClick={() => {
                      FaroInsightsWrapper.pushEvent("check_out_sign")
                      handleSigning("signing")
                    }}>
                    {t("patient.conclude.sign")}
                  </Button>

                  {isElectronicPrescription && (
                    <Button
                      data-testid="generate-electronic-prescription-button"
                      disabled={processingDocuments.size > 0}
                      className={cn("ml-2 min-w-19.5 px-6", isQES && "rounded-r-none")}
                      isLoading={processingAction === "electronic"}
                      onClick={() => {
                        FaroInsightsWrapper.pushEvent("check_out_electronic_prescription")
                        handleElectronicPrescription()
                      }}>
                      {t("patient.conclude.electronicPrescription")}
                    </Button>
                  )}

                  {isQES && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="primary" className="border-l-surface-subdued rounded-l-none border-l px-2">
                          <CaretDown size={20} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-lg p-2">
                        <DropdownMenuItem asChild className="w-full justify-start py-2" onClick={handleQESCancel}>
                          <Button variant="tertiary" disabled={!processingDocuments?.has(document.id)}>
                            {t("common.button.cancelRequest")}
                          </Button>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {renderViewer()}
    </div>
  )
}

export { DocumentViewer, PdfDocumentViewer }
